import React from 'react';
import { LuPlus } from 'react-icons/lu';

import { WashSaleGroup } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getPersonOrgName, useRequiredParams } from 'utils';
import { usePersonOrgProfile } from 'views/person/person-queries';
import { usePortfolioProfiles, useWashSaleGroups } from 'views/portfolio/portfolio-queries';
import { PortfolioProfileTable } from 'views/portfolio/PortfolioProfileTable';
import { HorizontalLayout, Spinner, Tag, Text, VerticalLayout } from 'widgets';

import { HouseholdHeaderActionsPortal } from '../HouseholdIndividualPage';

export const WashSaleGroupsList = ({
    onCreate,
    onEdit,
}: {
    onCreate: () => void;
    onEdit: (washSaleGroup: WashSaleGroup) => void;
}) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: washSaleGroups } = useWashSaleGroups(globalAccountGUID);

    return (
        <VerticalLayout $gap={32} $padding="32px">
            <HouseholdHeaderActionsPortal>
                <Button variant="outline2" size="sm" onClick={onCreate} className="flex items-center">
                    <LuPlus className="h-4 w-4" />
                    Group
                </Button>
            </HouseholdHeaderActionsPortal>
            <VerticalLayout $gap={32}>
                {washSaleGroups?.map((washSaleGroup, idx) => (
                    <WashSaleGroupListItem key={idx} washSaleGroup={washSaleGroup} onEdit={onEdit} />
                ))}
            </VerticalLayout>
        </VerticalLayout>
    );
};

const WashSaleGroupListItem = ({
    washSaleGroup,
    onEdit,
}: {
    washSaleGroup: WashSaleGroup;
    onEdit: (washSaleGroup: WashSaleGroup) => void;
}) => {
    const { data: portfolioProfiles, isLoading } = usePortfolioProfiles(washSaleGroup.containedAccounts);

    const { data: personOrgProfile } = usePersonOrgProfile(
        washSaleGroup.washSaleGroupGUID ? undefined : washSaleGroup.containedPersonOrgs[0],
    );

    return (
        <VerticalLayout $gap={8}>
            <HorizontalLayout $apart $rowEnd>
                <Text largeBody>
                    {washSaleGroup.washSaleGroupGUID
                        ? washSaleGroup.name?.plainText
                        : getPersonOrgName(personOrgProfile?.personOrg)}
                </Text>

                <HorizontalLayout $gap={8}>
                    {washSaleGroup.washSaleGroupGUID ? (
                        <Button variant="secondary" size="sm" onClick={() => onEdit(washSaleGroup)}>
                            Manage
                        </Button>
                    ) : (
                        <Tag type="info">Auto</Tag>
                    )}
                </HorizontalLayout>
            </HorizontalLayout>
            {isLoading ? (
                <Spinner />
            ) : (
                portfolioProfiles &&
                portfolioProfiles.length > 0 && (
                    <Card>
                        <PortfolioProfileTable
                            portfolioProfiles={portfolioProfiles}
                            columnConfig={{ showGlobalAccount: true, showClient: true }}
                        />
                    </Card>
                )
            )}
        </VerticalLayout>
    );
};
